import { countryForTimezone } from '@/components/PhoneNumberField/phoneNumbers'

/**
 * The ticket in the language of the place the event is held, shown under the
 * English, so an attendee and the people at the door can both read it. The
 * place comes from the event's time zone (Europe/Madrid → Spain → Spanish).
 *
 * Only countries with one clear language get a translation: a zone such as
 * Europe/Brussels or Europe/Zurich, or an English-speaking country, gets none.
 */
export type TicketLanguage = 'es' | 'pt' | 'fr' | 'de' | 'it' | 'nl'

export type TicketPhrases = Readonly<{
  lang: TicketLanguage
  freeEntry: string
  guest: string
  invitedBy: (name: string) => string
  showCode: string
}>

const languageByCountry: Readonly<Record<string, TicketLanguage>> = {
  ES: 'es', MX: 'es', AR: 'es', CO: 'es', PE: 'es', CL: 'es', VE: 'es', EC: 'es', GT: 'es', CU: 'es', BO: 'es', DO: 'es', HN: 'es', PY: 'es', SV: 'es', NI: 'es', CR: 'es', PA: 'es', UY: 'es',
  PT: 'pt', BR: 'pt', AO: 'pt', MZ: 'pt',
  FR: 'fr', MC: 'fr',
  DE: 'de', AT: 'de', LI: 'de',
  IT: 'it', SM: 'it',
  NL: 'nl',
}

const phrases: Readonly<Record<TicketLanguage, TicketPhrases>> = {
  es: { lang: 'es', freeEntry: 'Entrada gratuita', guest: 'Invitado', invitedBy: (name) => `Te invita ${name}`, showCode: 'Muestra este código en la entrada' },
  pt: { lang: 'pt', freeEntry: 'Entrada gratuita', guest: 'Convidado', invitedBy: (name) => `Convite de ${name}`, showCode: 'Mostre este código na entrada' },
  fr: { lang: 'fr', freeEntry: 'Entrée gratuite', guest: 'Invité', invitedBy: (name) => `Invitation de ${name}`, showCode: 'Présentez ce code à l’entrée' },
  de: { lang: 'de', freeEntry: 'Freier Eintritt', guest: 'Gast', invitedBy: (name) => `Eingeladen von ${name}`, showCode: 'Zeigen Sie diesen Code am Eingang' },
  it: { lang: 'it', freeEntry: 'Ingresso gratuito', guest: 'Ospite', invitedBy: (name) => `Invito di ${name}`, showCode: 'Mostra questo codice all’ingresso' },
  nl: { lang: 'nl', freeEntry: 'Gratis toegang', guest: 'Gast', invitedBy: (name) => `Uitgenodigd door ${name}`, showCode: 'Laat deze code zien bij de ingang' },
}

/** The ticket's second language from the event's time zone, or null when English alone fits. */
export function ticketPhrasesFor(timezone: string | null | undefined): TicketPhrases | null {
  const country = countryForTimezone(timezone)
  const language = country ? languageByCountry[country] : undefined
  return language ? phrases[language] : null
}

const atNoon = (value: string) => new Date(`${value}T12:00:00`)

/** The date written the local way ("martes, 1 de diciembre de 2026"); a range for a multi-day event. */
export function nativeTicketDate(lang: TicketLanguage, eventDate: string, endDate: string | null) {
  if (!endDate || endDate === eventDate) {
    return new Intl.DateTimeFormat(lang, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }).format(atNoon(eventDate))
  }
  const short = new Intl.DateTimeFormat(lang, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })
  return `${short.format(atNoon(eventDate))} – ${short.format(atNoon(endDate))}`
}
